/**
 * Pure helpers for the Records panel. Grouping and date formatting only read
 * the record; the model (startup payload) never supplies values or dates.
 */

export type MedicationGroup = 'scheduled' | 'asNeeded' | 'continuous';

/** Text fallback for a medication's group when the startup payload gave none. */
export function inferMedicationGroup(med: { name: string; dosage?: string }): MedicationGroup {
  const text = `${med.name} ${med.dosage ?? ''}`;
  if (/as needed|\bprn\b/i.test(text)) return 'asNeeded';
  if (/continuous|\/hr\b|per hour|infusion/i.test(text)) return 'continuous';
  return 'scheduled';
}

/** FHIR MedicationRequest statuses other than active go under "Stopped". */
export function isActiveMedication(status?: string): boolean {
  return !status || status.toLowerCase() === 'active';
}

/** Parse a record date; date-only values ("YYYY-MM-DD") are read as local dates to avoid a UTC shift. */
export function parseRecordDate(value?: string): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * "This admission" when the onset is on or after the admission date; without
 * either date, an active condition counts as this admission.
 */
export function isThisAdmission(
  condition: { onsetDate?: string; clinicalStatus?: string },
  admissionDate?: string,
): boolean {
  const onset = parseRecordDate(condition.onsetDate);
  const admission = parseRecordDate(admissionDate);
  if (onset && admission) return onset.getTime() >= admission.getTime();
  return (condition.clinicalStatus || '').toLowerCase() === 'active';
}

export function formatRecordDate(value: string | undefined, language: string): string {
  const parsed = parseRecordDate(value);
  if (!parsed) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(parsed);
}

export function formatRecordDateTime(value: string | undefined, language: string): string {
  const parsed = parseRecordDate(value);
  if (!parsed) return '';
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

/** Time only when generated today, otherwise date and time. Null when the timestamp is unparseable. */
export function formatGeneratedAt(iso: string, language: string): string | null {
  const generated = new Date(iso);
  if (Number.isNaN(generated.getTime())) return null;
  const isToday = generated.toDateString() === new Date().toDateString();
  return new Intl.DateTimeFormat(
    language,
    isToday ? { timeStyle: 'short' } : { dateStyle: 'medium', timeStyle: 'short' },
  ).format(generated);
}

/** Matches the data-testid convention used by the record components. */
export function slugify(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

export function formatRecordTime(value: string | undefined, language: string): string {
  const parsed = parseRecordDate(value);
  if (!parsed) return '';
  return new Intl.DateTimeFormat(language, { timeStyle: 'short' }).format(parsed);
}

/**
 * "today", "yesterday", or the local date (YYYY-MM-DD) for grouping rows by
 * day; null when the record date does not parse.
 */
export function dayGroupId(value: string | undefined, now: Date = new Date()): string | null {
  const parsed = parseRecordDate(value);
  if (!parsed) return null;
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const daysAgo = Math.round((startOfDay(now) - startOfDay(parsed)) / (24 * 60 * 60 * 1000));
  if (daysAgo === 0) return 'today';
  if (daysAgo === 1) return 'yesterday';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** True when `value` falls inside the `hours` window ending at `reference` (the record's newest entry). */
export function isWithinHoursOf(value: string | undefined, reference: string | undefined, hours: number): boolean {
  const parsed = parseRecordDate(value);
  const ref = parseRecordDate(reference);
  if (!parsed || !ref) return false;
  return ref.getTime() - parsed.getTime() <= hours * 60 * 60 * 1000;
}

/** Common Epic note types to plain-language label keys; unknown types show as recorded. */
const NOTE_TYPE_KEYS: Array<[RegExp, string]> = [
  [/progress/i, 'records.noteTypeProgress'],
  [/h\s*&\s*p|history\s*(and|&)\s*physical/i, 'records.noteTypeAdmission'],
  [/consult/i, 'records.noteTypeConsult'],
  [/discharge/i, 'records.noteTypeDischarge'],
  [/nursing|\bnurse\b/i, 'records.noteTypeNursing'],
  [/respiratory/i, 'records.noteTypeRespiratory'],
];

export function noteTypeKey(noteType: string): string | null {
  const match = NOTE_TYPE_KEYS.find(([pattern]) => pattern.test(noteType || ''));
  return match ? match[1] : null;
}

/** Care team roles to plain-language label keys (a `Desc` key holds the static description). */
const ROLE_KEYS: Array<[RegExp, string]> = [
  [/attending/i, 'records.roleAttending'],
  [/intensivist/i, 'records.roleIntensivist'],
  [/hospitalist/i, 'records.roleHospitalist'],
  [/fellow/i, 'records.roleFellow'],
  [/resident/i, 'records.roleResident'],
  [/respiratory/i, 'records.roleRespiratory'],
  [/registered nurse|\brn\b|\bnurse\b(?!\s*practitioner)/i, 'records.roleNurse'],
  [/pharmac/i, 'records.rolePharmacist'],
  [/social work/i, 'records.roleSocialWorker'],
  [/dietitian|dietician|nutrition/i, 'records.roleDietitian'],
  [/child life/i, 'records.roleChildLife'],
];

export function roleKey(role: string): string | null {
  const match = ROLE_KEYS.find(([pattern]) => pattern.test(role || ''));
  return match ? match[1] : null;
}

/** Roles that work at the bedside or on the unit, as opposed to consulting services. */
export function isBedsideRole(roles: string[]): boolean {
  return /attending|fellow|resident|nurse|respiratory|hospitalist|intensivist/i.test(roles.join(' '));
}

/** The attending and the bedside nurse are the parent's first call. */
export function isFirstCallRole(roles: string[]): boolean {
  return roles.some((role) => {
    const key = roleKey(role);
    return key === 'records.roleAttending' || key === 'records.roleNurse';
  });
}

/** Oxygen flow and FiO2 readings describe breathing support rather than the child's own vitals. */
export function isBreathingSupportVital(type: string): boolean {
  return /oxygen flow|fio2|inhaled oxygen|oxygen setting|high.?flow/i.test(type || '');
}
